import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { RefreshCw, LogIn, ClipboardList, Pencil, Trash2, Package, CheckSquare, Upload, DollarSign, Activity, UserCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_META = {
  login: { label: 'Login', color: 'bg-sky-100 text-sky-700', icon: LogIn },
  order_create: { label: 'Order Created', color: 'bg-green-100 text-green-700', icon: ClipboardList },
  order_edit: { label: 'Order Updated', color: 'bg-amber-100 text-amber-700', icon: Pencil },
  order_delete: { label: 'Order Deleted', color: 'bg-red-100 text-red-700', icon: Trash2 },
  order_receive: { label: 'Stock Received', color: 'bg-teal-100 text-teal-700', icon: Package },
  order_complete: { label: 'Order Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckSquare },
  stock_upload: { label: 'Stock Upload', color: 'bg-indigo-100 text-indigo-700', icon: Upload },
  price_list_edit: { label: 'Price Updated', color: 'bg-amber-100 text-amber-700', icon: DollarSign },
  price_list_delete: { label: 'Price Deleted', color: 'bg-red-100 text-red-700', icon: Trash2 },
  price_list_upload: { label: 'Price List Upload', color: 'bg-indigo-100 text-indigo-700', icon: Upload },
  todo_create: { label: 'Task Created', color: 'bg-green-100 text-green-700', icon: CheckSquare },
  todo_update: { label: 'Task Updated', color: 'bg-amber-100 text-amber-700', icon: Pencil },
  todo_delete: { label: 'Task Deleted', color: 'bg-red-100 text-red-700', icon: Trash2 },
  impersonate_start: { label: 'Impersonation', color: 'bg-purple-100 text-purple-700', icon: UserCheck },
};

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700',
  winlife: 'bg-orange-100 text-orange-700',
  srh: 'bg-cyan-100 text-cyan-700',
  user: 'bg-slate-100 text-slate-600',
};

const timeAgo = (ts) => {
  if (!ts) return '';
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function ActivityFeed({ user }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [usernames, setUsernames] = useState([]);
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({ username: '', action: '', start_date: '', end_date: '' });
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      params.append('limit', limit);
      const res = await axios.get(`${API}/activity/feed?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setUsernames(res.data.usernames || []);
      setActions(res.data.actions || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load activity feed');
    } finally {
      setLoading(false);
    }
  }, [filters, limit, user.token]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  return (
    <div data-testid="activity-feed" className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-4 sm:px-8 py-6">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-white" />
          <div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Activity Feed</h2>
            <p className="text-slate-300 mt-1 text-sm">Track every action by ADMIN and WINLIFE — orders, uploads, price list, tasks and logins</p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-5 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">User</label>
            <select
              data-testid="activity-filter-user"
              value={filters.username}
              onChange={(e) => setFilter('username', e.target.value)}
              className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">All users</option>
              {usernames.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Action</label>
            <select
              data-testid="activity-filter-action"
              value={filters.action}
              onChange={(e) => setFilter('action', e.target.value)}
              className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">All actions</option>
              {actions.map(a => <option key={a} value={a}>{ACTION_META[a]?.label || a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
            <input
              data-testid="activity-filter-start-date"
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilter('start_date', e.target.value)}
              className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
            <input
              data-testid="activity-filter-end-date"
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilter('end_date', e.target.value)}
              className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <Button
            data-testid="activity-refresh-btn"
            onClick={fetchFeed}
            variant="outline"
            size="sm"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {(filters.username || filters.action || filters.start_date || filters.end_date) && (
            <Button
              data-testid="activity-clear-filters-btn"
              onClick={() => setFilters({ username: '', action: '', start_date: '', end_date: '' })}
              variant="ghost"
              size="sm"
            >
              Clear filters
            </Button>
          )}
        </div>

        <p data-testid="activity-total" className="text-xs text-slate-500 mb-3">{total} entr{total === 1 ? 'y' : 'ies'} found</p>

        {/* Feed list */}
        <div className="space-y-2">
          {logs.length === 0 && !loading && (
            <div data-testid="activity-empty" className="text-center py-12 text-slate-400">
              <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No activity recorded yet. Actions will appear here as they happen.</p>
            </div>
          )}
          {logs.map((log) => {
            const meta = ACTION_META[log.action] || { label: log.action, color: 'bg-slate-100 text-slate-600', icon: Activity };
            const Icon = meta.icon;
            return (
              <div
                key={log.id}
                data-testid={`activity-entry-${log.id}`}
                className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <div className={`p-2 rounded-lg shrink-0 ${meta.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${ROLE_COLORS[log.role] || ROLE_COLORS.user}`}>
                      {log.username}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${meta.color}`}>
                      {meta.label}
                    </span>
                    {log.impersonated_by && (
                      <span className="px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-600">
                        via {log.impersonated_by}
                      </span>
                    )}
                  </div>
                  {log.details && (
                    <p className="text-sm text-slate-700 mt-1 break-words">{log.details}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-slate-500">{timeAgo(log.timestamp)}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {total > limit && (
          <div className="text-center mt-4">
            <Button
              data-testid="activity-load-more-btn"
              onClick={() => setLimit(prev => prev + 50)}
              variant="outline"
              size="sm"
            >
              Load more ({total - limit} remaining)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
